import { Header } from '@/components';
import { useCustomThemeContext } from '@/components/context';
import config from '@/config';
import { ConfigError, Conversation, UserSignin, ValidateOTP } from '@/containers';
import { sessionAPI } from '@/libs/api';
import { appStore, useAppStore } from '@/store';
import { isChannelMyGP, isUserAgentMobile, isUserAgentMyGP } from '@/utils/helpers';
import type { NextPage, NextPageContext } from 'next';
import { parseCookies, setCookie } from 'nookies';
import { FC, memo, useCallback, useEffect, useState } from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare let window: Window;
declare let jsInterface: jsInterface;
interface Webkit {
  messageHandlers: {
    onTokenStatus: {
      postMessage(message: any): void;
    };
  };
}

interface Window {
  webkit: Webkit;
  alert(message: string): void;
}

interface jsInterface {
  tokenStatus(status: boolean, message: string): void;
}

type Props = {
  data?: IAPI.UserSessionResponse;
  token?: string;
  isMobile: boolean;
  isMyGP: boolean;
  mevrik_ref?: string;
  referrer?: string;
  channel?: string;
  isChatHistory?: boolean;
  error?: boolean;
};

const WidgetScreen: FC = memo(() => {
  const { activeScreen } = useAppStore();

  switch (activeScreen) {
    case 'USER_SIGNED_IN':
      return <UserSignin />;
    case 'OTP_VERIFICATION':
      return <ValidateOTP />;
    case 'CONVERSATION':
      return <Conversation />;
    default:
      return <UserSignin />;
  }
});

const Home: NextPage<Props> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialValues, setInitialValues] = useState<Props>(props);
  const updateTheme = useCustomThemeContext();

  const afterHydrate = useCallback(() => {
    const { data, error, token, isMobile, isMyGP, mevrik_ref, isChatHistory } = initialValues;

    if (token) {
      appStore.setToken(token);
    }

    if (token && error) {
      // appStore.setToken(token);
      const message = 'Token expired or invalid';
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.onTokenStatus) {
        window.webkit.messageHandlers.onTokenStatus.postMessage({ message });
      } else {
        // jsInterface.tokenStatus(false, message);
      }
    } else if (data) {
      updateTheme(data);
      appStore.setUserSession(data);
    }

    if ((data?.mode === 'USER' && token) || (mevrik_ref && isChatHistory)) {
      appStore.setActiveScreen('CONVERSATION');
    }

    appStore.setIsMobile(isMobile);
    appStore.setIsMyGP(isMyGP);
  }, [initialValues, updateTheme]);

  const hydrate = useCallback(async () => {
    if (!initialValues?.token) {
      const { mevrik_ref, referrer = 'www.grameenphone.com', channel = 'gp-website' } = initialValues;
      let newToken: string | undefined;

      try {
        if (mevrik_ref) {
          const { success, data } = await sessionAPI.iWasHere(mevrik_ref);
          if (success && data?.token) {
            newToken = data.token;
          }
        }

        if (!newToken) {
          const { success, data } = await sessionAPI.initChatByReferrer(referrer, channel);
          if (success && data?.token) {
            newToken = data.token;
          }
        }

        if (newToken) {
          const { success } = await sessionAPI.ping(newToken);

          if (success) {
            const { success, data } = await sessionAPI.getUserSession(newToken);
            if (success && data) {
              if (data?.user.case_id) {
                setCookie(null, 'mevrik_ref', data.user.unique_id, {
                  maxAge: 15 * 60,
                  path: '/',
                  sameSite: 'None',
                  secure: true,
                });
              }

              setInitialValues((prev) => ({
                ...prev,
                data,
                token: newToken,
                isChatHistory: !!data?.user.case_id,
                error: false,
              }));
            } else {
              throw new Error('User information not found');
            }
          } else {
            throw new Error('Ping failed');
          }
        } else {
          throw new Error('Token not found');
        }
      } catch (error) {
        console.log(error);
      } finally {
        afterHydrate();
        setIsLoading(false);
      }
    } else {
      afterHydrate();
      setIsLoading(false);
    }
  }, [afterHydrate, initialValues]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const {
    userSession: {
      persona: { name, logo },
    },
    isSessionActive,
  } = useAppStore();

  if (isLoading) return <div>Loading...</div>;

  if (!isLoading && !initialValues?.data) return <ConfigError />;

  return (
    <div className="WidgetContainer">
      <Header isActive={isSessionActive} name={name} logo={logo} />
      <WidgetScreen />
    </div>
  );
};

/**
 *
 *    I N I T I A L    P R O P S
 *
 *
 * @param ctx NextPageContext
 * @returns Promise
 */
Home.getInitialProps = async (ctx: NextPageContext): Promise<Props> => {
  const { query, req } = ctx;

  let token = (query?.token ?? req?.headers?.token ?? req?.headers['x-mevrik-token']) as string;

  let referrer = query?.utm_source as string;

  const action = query?.action as string;

  let channel = (query?.channel as string) ?? config.defaultChannel;

  const userAgent = req?.headers['user-agent'] || '';
  const isMobile = isUserAgentMobile(userAgent);
  let isMyGP = isUserAgentMyGP(userAgent);
  if (!isMyGP) {
    isMyGP = isChannelMyGP(channel || '');
  }
  // console.log({ channel, userAgent, isMobile, isMyGP });

  let isChatHistory = false;

  const cookies = parseCookies({ req });
  let mevrik_ref = cookies['mevrik_ref'] as string;

  // clear cookie handle is client action is clear
  if (action == 'clear' && mevrik_ref) {
    mevrik_ref = '';
  }

  const getTokenFromVisitRef = async (visitRef: string) => {
    try {
      const { success, data } = await sessionAPI.iWasHere(visitRef);
      if (success && data.token) {
        return data.token;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  };

  const getTokenByReferrer = async (referrer: string) => {
    try {
      console.log('[session] verify by referrer: ' + referrer);
      const { success, data } = await sessionAPI.initChatByReferrer(referrer, channel as string);
      if (success) {
        return data.token;
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  };

  const getUserSession = async (token: string) => {
    try {
      const { success, data } = await sessionAPI.getUserSession(token);
      if (success) return data;
      throw new Error('Failed to fetch session data');
    } catch (error) {
      return undefined;
    }
  };

  // if token exists than clear mevrik_ref
  if (token && mevrik_ref) {
    mevrik_ref = '';
  }

  if (!token && mevrik_ref && mevrik_ref.length > 0) {
    token = (await getTokenFromVisitRef(mevrik_ref)) as string;
    // isChatHistory = false;
    // if (token) {
    //   const { success, data } = await sessionAPI.checkChatHistory(token);
    //   if (success && data?.data?.length) {
    //     isChatHistory = true;
    //   }
    // }
    if (token) {
      console.log('[session] rehydrate from session cookie ' + mevrik_ref);
    } else {
      console.log('[session] no session found for ' + mevrik_ref);
    }
  }

  if (!token && !referrer) {
    referrer = 'www.grameenphone.com';
    channel = 'gp-website';
  }

  if (!token && referrer) {
    token = (await getTokenByReferrer(referrer)) as string;
  }

  if (token) {
    try {
      const { success } = await sessionAPI.ping(token);
      if (success) {
        const userSession = await getUserSession(token);

        // check if user had recently initiated any conversaion
        if (userSession?.user.case_id) {
          console.log('[session] serving case ', userSession?.user.case_id);
        }

        isChatHistory = !!userSession?.user.case_id;

        // if (!mevrik_ref && userSession?.user.unique_id) {
        if (userSession?.user.unique_id) {
          setCookie(ctx, 'mevrik_ref', userSession?.user.unique_id, {
            maxAge: 15 * 60,
            path: '/',
            sameSite: 'None',
            secure: true,
          });
        }
        return { data: userSession, token, isMobile, isMyGP, mevrik_ref, isChatHistory, error: false };
      }
      throw new Error('Token expired or invalid');
    } catch (error) {
      return { data: undefined, token, isMobile, isMyGP, mevrik_ref, isChatHistory, error: true };
    }
  }
  return { data: undefined, token, isMobile, isMyGP, mevrik_ref, isChatHistory, error: false };
};

export default Home;
